import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslations } from "@/hooks/useTranslations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import { isValidLocaleSegment } from "@/utils/localeRouteHelpers";
import { getCachedSupportedLocales } from "@/services/supportedLocalesService";
import { DocumentErrorRegular, LockClosedRegular } from "@fluentui/react-icons";
import { GenericErrorPage } from "@/pages/ErrorPage/GenericErrorPage";
import styles from "./GenericErrorPage.module.scss";

import notFoundLightMode from "@/assets/not-found-light-mode.png";
import notFoundDarkMode from "@/assets/not-found-dark-mode.png";

type ErrorPageVariant = "generic" | "notFound" | "contentNotFound" | "unauthorized";

interface IErrorPageProps {
  variant?: ErrorPageVariant;
  message?: string;
  title?: string;
  markdown?: string;
}

export function ErrorPage({ 
  variant = "generic",
  message, 
  title,
  markdown,
}: IErrorPageProps): ReactNode {
  const params = useParams();
  const { t } = useTranslations();

  let defaultTitle: string;
  let defaultMessage: string;
  switch (variant) {
    case "notFound":
      defaultTitle = t("notFound.title");
      defaultMessage = t("notFound.message");
      break;
    case "contentNotFound":
      defaultTitle = t("errorPage.title");
      defaultMessage = t("errorPage.message");
      break;
    case "unauthorized":
      defaultTitle = t("unauthorized.title");
      defaultMessage = t("unauthorized.defaultMessage");
      break;
    default:
      defaultTitle = t("errorPage.title");
      defaultMessage = t("errorPage.message");
      break;
  }

  const displayTitle = title ?? defaultTitle;
  const displayMessage = message ?? defaultMessage;
  const markdownContent = markdown ?? `# ${displayTitle}\n\n${displayMessage}`;

  usePageTitle(displayTitle);

  const storedLocale = getLocalePreference()?.localeCode;
  const storedLocaleOrDefault = isValidLocaleSegment(storedLocale) ? storedLocale : "en-US";

  const homeLocale = (() => {
    if (variant !== "notFound") {
      return isValidLocaleSegment(params.locale) ? params.locale : storedLocaleOrDefault;
    }

    const supportedLocales = getCachedSupportedLocales();
    const isUrlLocaleSupported =
      isValidLocaleSegment(params.locale) &&
      supportedLocales !== null &&
      params.locale !== undefined &&
      supportedLocales.has(params.locale.toLowerCase());

    return isUrlLocaleSupported ? params.locale && params.locale.toLowerCase() : storedLocaleOrDefault;
  })();

  const actions =
    variant === "notFound" || (variant === "generic" && !isValidLocaleSegment(params.locale)) ? (
      <Link className={styles.primaryAction} to={`/${homeLocale}`}>
        {t("notFound.goHome")}
      </Link>
    ) : null;

  const icon =
    variant === "unauthorized" ? (
      <LockClosedRegular aria-hidden="true" />
    ) : (
      <DocumentErrorRegular aria-hidden="true" />
    );

  const illustration =
    variant === "notFound" || variant === "contentNotFound" ? (
      <>
        <img
          className={styles.notFoundImageLight}
          src={notFoundLightMode}
          alt=""
          role="presentation"
        />
        <img
          className={styles.notFoundImageDark}
          src={notFoundDarkMode}
          alt=""
          role="presentation"
        />
      </>
    ) : undefined;

  return (
    <GenericErrorPage
      icon={icon}
      illustration={illustration}
      markdown={markdownContent}
      actions={actions}
    />
  );
}
